import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING,
    LIST, ADD, EDIT,
    DANGER, SUCCESS,
} from "const"
import { StateType, deepCopy, hasAccess, hasProjectAccess } from "client/utils"
import { silSubsystemsRoute } from "client/parts/routes"
import {
    PRM_SIL_DEVICE,
    PRM_SIL_SUBSYSTEM,
    StoreType,
    SilDeviceType,
    SilDeviceList,
    SilDeviceFilters,

    LoadAllSilDevices,
    ChangeSilDeviceStatus,
    DeleteSilDevice,
    SortSilDevices,
    GetMaxSilDevices,
    GetMaxSilSubsystems,

    setSilDeviceList,
    setSilDeviceIds,
    setSilDeviceSort,
    setSilDeviceFilters,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    Loader,
    Button,
    Feedback,
    Table,
    TableSearchWrapper,
    SortRows,
    ChangeStatus,
    DeleteRow,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import SilDeviceSearch from "client/pages/search/silDeviceSearch"
import Columns from "./columns"


const List = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const baseDeviceRoute = useSelector((state: StoreType) => state.silDevice.baseDeviceRoute)
    const baseSubsystemRoute = useSelector((state: StoreType) => state.silDevice.baseSubsystemRoute)

    const deviceList = useSelector((state: StoreType) => state.silDevice.deviceList)
    const deviceIds = useSelector((state: StoreType) => state.silDevice.deviceIds)
    const sortColumn = useSelector((state: StoreType) => state.silDevice.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.silDevice.sortOrder)
    const filters = useSelector((state: StoreType) => state.silDevice.filters)
    const reloadList = useSelector((state: StoreType) => state.silDevice.reloadList)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const isSubsystem = window.location.pathname.indexOf(silSubsystemsRoute) >= 0

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllSilDevices(userData.accountId, project.id, isSubsystem).then((res) => {
            dispatch(setSilDeviceList(res))
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedIds = SortSilDevices(Object.values(deviceList), sortColumn, sortOrder, filters)
        dispatch(setSilDeviceIds(sortedIds))
    }, [deviceList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof SilDeviceType, order) => {
        dispatch(setSilDeviceSort(column, order))
    }

    const onFilter = (newFilters: SilDeviceFilters) => {
        dispatch(setSilDeviceFilters(newFilters))
    }

    const onStatusChange: ChangeStatus = (device: SilDeviceType, callback) => {
        ChangeSilDeviceStatus(device).then((status) => {
            callback(() => {
                const deviceListCopy: SilDeviceList = deepCopy(deviceList)
                deviceListCopy[device.id] = {
                    ...deviceListCopy[device.id],
                    status,
                }
                dispatch(setSilDeviceList(deviceListCopy))
            })
        })
    }

    const onDelete: DeleteRow = (device: SilDeviceType, callback) => {
        DeleteSilDevice(device.id, userData.accountId, project.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const deviceListCopy: SilDeviceList = deviceList ? deepCopy(deviceList) : {}
                delete deviceListCopy[device.id]
                dispatch(setSilDeviceList(deviceListCopy))
                dispatch(setSilDeviceIds(deviceIds.filter((id) => id !== device.id)))
            })
        })
    }


    // max number of devices
    const devicesNumber = Object.keys(deviceList).length
    const { maxDevices, deviceFeedback } = GetMaxSilDevices(packageSettings, userData, devicesNumber, lang)
    const { maxSubsystems, subsystemFeedback } = GetMaxSilSubsystems(packageSettings, userData, devicesNumber, lang)

    const maxDevicesReached = maxDevices !== null && devicesNumber >= maxDevices
    const maxDevicesExceeded = maxDevices !== null && devicesNumber > maxDevices

    const maxSubsystemsReached = maxSubsystems !== null && devicesNumber >= maxSubsystems
    const maxSubsystemsExceeded = maxSubsystems !== null && devicesNumber > maxSubsystems


    const columns = Columns()

    const permission = isSubsystem ? PRM_SIL_SUBSYSTEM : PRM_SIL_DEVICE
    const listAccess = !project.id
        ? hasAccess(permission, LIST)
        : hasProjectAccess(permission, LIST)

    const addAccess = !project.id
        ? hasAccess(permission, ADD)
        : hasProjectAccess(permission, ADD)

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{isSubsystem ? lang.subsystems : lang.devices}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <TableSearchWrapper>
                    <SilDeviceSearch
                        id="deviceList"
                        rightOffset={addAccess}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        addAccess && (
                            <Button
                                variant="success"
                                disabled={!project.id && (isSubsystem ? maxSubsystemsReached : maxDevicesReached)}
                                onClick={() => dispatch(addModal({
                                    url: `${isSubsystem ? baseSubsystemRoute : baseDeviceRoute}/${EDIT}/0`,
                                }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={deviceList}
                    dataIds={deviceIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="md"
                    showPagination
                    onSort={onSort}
                    onStatusChange={onStatusChange}
                    onDelete={onDelete}
                />
                {
                    !project.id && userData.accountId !== 1 && (
                        <PageForm>
                            {
                                isSubsystem
                                && <Feedback variant={maxSubsystemsExceeded ? DANGER : SUCCESS}>{subsystemFeedback}</Feedback>
                            }
                            {
                                !isSubsystem
                                && <Feedback variant={maxDevicesExceeded ? DANGER : SUCCESS}>{deviceFeedback}</Feedback>
                            }
                        </PageForm>
                    )
                }
            </PageContent>
        </Container>
    )
}
export default List
